import PageLayout from "../../layouts/PageLayout";
import {Card, SubCard} from "../../components/card/Card.tsx";
import {
    useWriteEscrowFactoryAddWhitelistedToken,
    useWriteEscrowFactoryRemoveWhitelistedToken,
} from "../../generated.ts";
import {type Address, getAddress} from "viem";
import {type QueryClient, useQuery, useQueryClient,} from "@tanstack/react-query";
import {getWhitelistedTokens, type Token,} from "../../repository/TokenWhitelistRepository.ts";
import {type SubmitHandler, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {openExplorerInNewTab} from "../../utils/explorerHelper.ts";
import {useAccount, usePublicClient} from "wagmi";
import {useState} from "react";
import {StaffGuard} from "../../guards/StaffGuard.tsx";
import {ExternalLinkIcon, PlusIcon, TrashIcon} from "lucide-react";
import {StaffType} from "../../hooks/useStaffGuard.ts";

const optimisticAdd = (queryClient: QueryClient, address: string) => {
  queryClient.setQueryData(["whitelisted-tokens"], (old: Token[]) => [
    ...old,
    { address: address, symbol: null },
  ]);
};

const optimisticRemove = (queryClient: QueryClient, address: string) => {
  queryClient.setQueryData(["whitelisted-tokens"], (old: Token[]) =>
    [...old].filter((token) => token.address !== address),
  );
};

const TokenWhitelist = () => {
  const { data: whitelistedTokens, isLoading: isLoadingWhitelistedTokens } =
    useQuery({
      queryKey: ["whitelisted-tokens"],
      queryFn: () => getWhitelistedTokens(),
    });

  return (
    <PageLayout
      breadcrumbs={[
        { name: "Factory", href: "/factory" },
        { name: "Whitelist" },
      ]}
    >
        <StaffGuard staffTypes={[StaffType.CONTROLLER, StaffType.OWNER]}>

        <Card title={"Whitelist"}>
        <SubCard>
          <ListTokens
            isLoading={isLoadingWhitelistedTokens}
            tokens={whitelistedTokens || []}
          />
          <div className="divider" />
          <AddTokenForm />
        </SubCard>
      </Card>
        </StaffGuard>
    </PageLayout>
  );
};

const AddTokenForm = () => {
  const queryClient = useQueryClient();
  const publicClient = usePublicClient();
  const { address } = useAccount();
  const [isAddPending, setIsAddPending] = useState<boolean>(false);

  const { writeContractAsync: addWhitelistedToken } =
    useWriteEscrowFactoryAddWhitelistedToken();

  const { register, handleSubmit, reset, getValues } = useForm<{
    address: string;
  }>();

  const onSubmit: SubmitHandler<{ address: string }> = async (data) => {
    if (!address) return;
    if (!publicClient) return;

    setIsAddPending(true);
    const txid = await addWhitelistedToken({
      args: [data.address as Address],
    });

    await toast
      .promise(publicClient.waitForTransactionReceipt({ hash: txid }), {
        loading: "Waiting for transaction confirmation...",
        success: "Token added successfully!",
        error: "Failed to add token.",
      })
      .then(() => {
        optimisticAdd(queryClient, getValues("address"));
        reset();
        setIsAddPending(false);
      });
  };

  return (
    <form className="flex flex-row gap-4" onSubmit={handleSubmit(onSubmit)}>
      <input
        disabled={isAddPending}
        {...register("address", { required: true })}
        type="text"
        placeholder="Token Address"
        className="input input-bordered w-full mb-4"
      />
      <button disabled={isAddPending} type="submit" className="btn btn-primary">
        Add Token
        <PlusIcon />
      </button>
    </form>
  );
};

const ListTokens = ({
  tokens,
  isLoading,
}: {
  tokens: Token[];
  isLoading: boolean;
}) => {
  const queryClient = useQueryClient();
  const publicClient = usePublicClient();

  const { writeContractAsync: removeWhitelistedToken } =
    useWriteEscrowFactoryRemoveWhitelistedToken();

  const handleRemove = async (address: string) => {
    if (!address) return;
    if (!publicClient) return;

    const txid = await removeWhitelistedToken({
      args: [address as Address],
    });

    await toast
      .promise(publicClient.waitForTransactionReceipt({ hash: txid }), {
        loading: "Waiting for transaction confirmation...",
        success: "Token removed successfully!",
        error: "Failed to remove token.",
      })
      .then(() => {
        optimisticRemove(queryClient, address);
      });
  };

  const LoadingSkeleton = () => {
    return Array.from({ length: 4 }, (_, index) => (
      <tr key={index}>
        <td>
          <div className="skeleton h-10 w-full" />
        </td>
        <td className="w-full">
          <div className="skeleton h-10 w-full" />
        </td>
        <td className="flex flex-row gap-2">
          <div className="skeleton h-10 w-12" />
          <div className="skeleton h-10 w-12" />
        </td>
      </tr>
    ));
  };

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th>Token Name</th>
            <th>Token Address</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            tokens.map((token) => (
              <tr>
                <td>
                  {token.symbol ? (
                    token.symbol
                  ) : (
                    <span className="loading loading-spinner loading-xs"></span>
                  )}
                </td>
                <td className="w-full">{getAddress(token.address)}</td>
                <td className="flex flex-row gap-2">
                  <button
                    className="btn btn-sm"
                    onClick={() => openExplorerInNewTab(token.address)}
                  >
                    <ExternalLinkIcon />
                  </button>
                  <button
                    className="btn btn-error btn-sm text-white"
                    onClick={() => handleRemove(token.address)}
                  >
                    <TrashIcon />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TokenWhitelist;
